import {
  CBadge,
  CCloseButton,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import PlayerService from 'src/service/PlayerService'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import Notify from '../Notify'
import { resolvePlayerImageUrl } from 'src/utils/imageUrl'
import DetailsTab from './PlayerDetail/DetailsTab'
import ProfileTab from './PlayerDetail/ProfileTab'
import AvailabilityTab from './PlayerDetail/AvailabilityTab'
import SponsorshipTab from './PlayerDetail/SponsorshipTab'

const TABS = [
  { key: 'details', label: 'Details' },
  { key: 'profile', label: 'Profile' },
  { key: 'availability', label: 'Availability' },
  { key: 'sponsorship', label: 'Sponsorship' },
]

const PlayerDetailPanel = ({ playerId, visible, onClose, fetchUsers }) => {
  const [activeTab, setActiveTab] = useState('details')
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [statusShowConfirm, setStatusShowConfirm] = useState(false)
  const [pendingStatus, setPendingStatus] = useState(null)

  const loadDetail = () => {
    if (!playerId) return
    setLoading(true)
    PlayerService.getPlayerDetail(playerId).then((res) => {
      if (res.status === 200) {
        setDetail(res)
      }
      setLoading(false)
    })
  }

  useEffect(() => {
    if (visible && playerId) {
      setActiveTab('details')
      loadDetail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, playerId])

  // Shared save path for the Details + Profile tabs. editPlayer overwrites every field it
  // knows about on every call (it doesn't skip blank ones), so every submit must resend the
  // *current* full set with only the caller's own edits layered on top - otherwise saving from
  // one tab silently blanks out fields owned by the other tab.
  const saveEditPlayer = (overrides) => {
    const p = detail?.player_data || {}
    const formData = new FormData()
    formData.append('playerId', playerId)
    formData.append('first_name', overrides.first_name ?? p.first_name ?? '')
    formData.append('last_name', overrides.last_name ?? p.last_name ?? '')
    formData.append('position', overrides.position ?? p.position ?? '')
    formData.append('team', overrides.team ?? p.team_id ?? '')
    formData.append('value', overrides.value ?? p.svalue ?? '')
    formData.append('bat_style', overrides.bat_style ?? p.bat_style ?? '')
    formData.append('bowl_style', overrides.bowl_style ?? p.bowl_style ?? '')
    formData.append('sponsor_link', overrides.sponsor_link ?? p.sponsor_link ?? '')
    formData.append(
      'external_profile_label',
      overrides.external_profile_label ?? p.external_profile_label ?? '',
    )
    formData.append(
      'external_profile_link',
      overrides.external_profile_link ?? p.external_profile_link ?? '',
    )
    formData.append('description', overrides.description ?? p.description ?? '')
    return PlayerService.editPlayer(formData)
  }

  const handleSaved = () => {
    loadDetail()
    fetchUsers()
  }

  const requestStatusChange = (nextStatus) => {
    CommonService.checkItemExists({ id: playerId, type: 'player_status' }).then((res) => {
      if (res.status === 200) {
        if (res.data) {
          ToastComponent(res.message, 'error')
        } else {
          setPendingStatus(nextStatus)
          setStatusShowConfirm(true)
        }
      }
    })
  }

  const confirmStatusChange = () => {
    setStatusShowConfirm(false)
    PlayerService.updateStatus(playerId, pendingStatus).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        handleSaved()
      }
    })
  }

  const player = detail?.player_data
  const avatarUrl = player ? resolvePlayerImageUrl(player) : null

  return (
    <>
      <COffcanvas placement="end" visible={visible} onHide={onClose} style={{ width: 640 }}>
        <COffcanvasHeader className="align-items-start">
          <div className="d-flex align-items-center">
            {avatarUrl && (
              <img
                src={avatarUrl}
                alt={player?.full_name}
                width={44}
                height={44}
                className="rounded-circle me-3"
              />
            )}
            <div>
              <div className="d-flex align-items-center gap-2">
                <span className="fs-5 fw-semibold">{player?.full_name}</span>
                <CBadge color={player?.is_active === 1 ? 'success' : 'danger'}>
                  {player?.is_active === 1 ? 'Active' : 'Inactive'}
                </CBadge>
              </div>
              <div className="text-medium-emphasis small">PID {player?.id}</div>
            </div>
          </div>
          <CCloseButton className="text-reset" onClick={onClose} />
        </COffcanvasHeader>

        <ul className="nav nav-underline px-3">
          {TABS.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                type="button"
                className={`nav-link${activeTab === tab.key ? ' active fw-semibold' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        <COffcanvasBody>
          {!loading && detail && (
            <>
              {activeTab === 'details' && (
                <DetailsTab
                  detail={detail}
                  saveEditPlayer={saveEditPlayer}
                  onSaved={handleSaved}
                  onCancel={onClose}
                  onToggleStatus={requestStatusChange}
                />
              )}
              {activeTab === 'profile' && (
                <ProfileTab
                  detail={detail}
                  saveEditPlayer={saveEditPlayer}
                  onSaved={handleSaved}
                  onCancel={onClose}
                  onToggleStatus={requestStatusChange}
                />
              )}
              {activeTab === 'availability' && (
                <AvailabilityTab
                  playerId={playerId}
                  onSaved={handleSaved}
                  onCancel={onClose}
                  onToggleStatus={requestStatusChange}
                  isActive={player?.is_active === 1}
                />
              )}
              {activeTab === 'sponsorship' && (
                <SponsorshipTab
                  detail={detail}
                  playerId={playerId}
                  onSaved={handleSaved}
                  onCancel={onClose}
                  onToggleStatus={requestStatusChange}
                  isActive={player?.is_active === 1}
                />
              )}
            </>
          )}
        </COffcanvasBody>
      </COffcanvas>

      <Notify
        setShowConfirm={setStatusShowConfirm}
        showConfirm={statusShowConfirm}
        handleConfirm={confirmStatusChange}
        handleCancel={() => setStatusShowConfirm(false)}
        text="Are you sure you want to change this player's status?"
      />
    </>
  )
}

export default PlayerDetailPanel
